/* Front pocket personalization — checkout guard.
 *
 * Order #2339 (23 Sep 2026) reached checkout as a $399 bag carrying a full
 * embroidery spec, no artwork file and no service line. The customer had
 * configured personalization and then paid with Apple Pay straight from the
 * product page. Express checkout buttons never fire a form submit event: they
 * read the product form, take its hidden properties[] inputs, and go. The
 * artwork only exists as a Blob in memory and the $99 line is only added by
 * fpm.js's submit handler, so neither happened.
 *
 * This file closes those holes without touching fpm.js:
 *
 *   1. While personalization is in play, the express buttons are hidden AND
 *      clicks on them are swallowed, so the bypass is not reachable. Plain
 *      bags keep one-tap checkout.
 *   2. If personalization is on but unfinished, add-to-cart is blocked with a
 *      message.
 *   3. If a personalized add-to-cart reaches the bubble phase, fpm.js declined
 *      it (busy, errored, never loaded) and the artwork is not attached, so it
 *      is refused. (Order #2366, 2 Oct 2026.)
 *
 * ---------------------------------------------------------------------------
 * 4 Oct 2026 — order #2372 got through again, on the revamped theme.
 *
 * Same signature as #2339: Apple Pay from the product page, embroidery
 * properties on the bag line, no EMB line, no Artwork, no Approved Proof.
 *
 * The previous version decided whether to hide the wallet buttons from
 * exactly two signals: a `change` event on [data-fpm-on], and a 500 ms poll
 * that STOPPED after 30 seconds. Anything that switched personalization on
 * without firing `change` (the new "?personalize=1" deep links from the
 * homepage, fpm.js setting .checked in code) and happened after the first
 * 30 seconds left the class off and Apple Pay visible. Configuring text takes
 * longer than 30 seconds, so the window was wide open.
 *
 * Changes in this version:
 *
 *   - "Personalizing" is decided from every signal, not just the toggle:
 *     the toggle is on, OR fpm.js has staged [data-fpm-prop] inputs in the
 *     form, OR the toggle has not rendered yet and the URL asked for
 *     personalization. Any one is enough to hide the wallet.
 *   - The check never stops. It re-runs on a timer for the life of the page
 *     and on every input/change/click, so a programmatic .checked is caught
 *     within a fraction of a second.
 *   - Belt and braces: a capture-phase click/pointer/key listener on window
 *     swallows any activation of a wallet button while personalizing, even if
 *     the CSS somehow failed to hide it. Window capture runs before the
 *     button's own handlers, including ones inside its shadow root.
 */
(function () {
  "use strict";

  var CLS = "fpm-personalizing";

  var WALLET_SELECTORS = [
    ".shopify-payment-button",
    ".shopify-payment-button__button",
    "shopify-accelerated-checkout",
    "shopify-accelerated-checkout-cart",
    "[data-shopify=dynamic-checkout-cart]",
    "[data-shopify=payment-button]"
  ];

  // display:none on the host also hides shadow-DOM children, which is where
  // Shopify renders the actual Apple Pay / Google Pay buttons.
  var CSS = WALLET_SELECTORS.map(function (s) { return "html." + CLS + " " + s; }).join(",") +
    "{display:none !important}";

  function injectCss() {
    if (document.querySelector("style[data-fpm-guard]")) return;
    var s = document.createElement("style");
    s.setAttribute("data-fpm-guard", "");
    s.appendChild(document.createTextNode(CSS));
    (document.head || document.documentElement).appendChild(s);
  }

  function toggleEl() { return document.querySelector("[data-fpm-on]"); }
  function isOn() { var t = toggleEl(); return !!(t && t.checked); }

  function cartForm() { return document.querySelector('form[action*="/cart/add"]'); }

  // fpm.js writes [data-fpm-prop] inputs only when its ready() passes.
  function hasStagedPersonalization(form) {
    if (form) return !!form.querySelector("[data-fpm-prop]");
    return !!document.querySelector('form[action*="/cart/add"] [data-fpm-prop]');
  }
  function isReady() { return hasStagedPersonalization(cartForm()); }

  // Homepage and ads deep-link to /products/tour-caddy?personalize=1. Until
  // the toggle exists, trust the URL; once it exists, the toggle decides.
  function urlAsksToPersonalize() {
    try { return /(?:^|[?&])personalize=1(?:&|$)/.test(window.location.search); }
    catch (_) { return false; }
  }

  function personalizing() {
    if (isOn() || hasStagedPersonalization()) return true;
    return !toggleEl() && urlAsksToPersonalize();
  }

  function isCartAdd(form) {
    return !!(form && form.action && form.action.indexOf("/cart/add") !== -1);
  }

  function setClass(on) {
    var r = document.documentElement;
    if (r.classList) { r.classList.toggle(CLS, on); return; }
    var c = (" " + r.className + " ").replace(" " + CLS + " ", " ").replace(/\s+/g, " ").trim();
    r.className = on ? (c ? c + " " + CLS : CLS) : c;
  }

  function sync() { setClass(personalizing()); }

  function showError(msg) {
    var e = document.querySelector("[data-fpm-error]");
    if (!e) { if (msg) window.alert(msg); return; }
    e.textContent = msg;
    if (msg) e.removeAttribute("hidden"); else e.setAttribute("hidden", "");
  }

  function focusPanel() {
    var panel = document.querySelector("[data-fpm-panel]") || toggleEl();
    if (panel && panel.scrollIntoView) {
      try { panel.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (_) {}
    }
  }

  // ---- 1. Wallet buttons: swallow activation while personalizing ----------

  function matchesWallet(el) {
    if (!el || el.nodeType !== 1) return false;
    for (var i = 0; i < WALLET_SELECTORS.length; i++) {
      try { if (el.matches(WALLET_SELECTORS[i])) return true; } catch (_) {}
    }
    return false;
  }

  function hitsWallet(e) {
    var path = e.composedPath ? e.composedPath() : null;
    if (path && path.length) {
      for (var i = 0; i < path.length; i++) if (matchesWallet(path[i])) return true;
      return false;
    }
    for (var n = e.target; n && n !== document; n = n.parentNode) if (matchesWallet(n)) return true;
    return false;
  }

  function blockWallet(e) {
    if (e.type === "keydown" && e.key !== "Enter" && e.key !== " ") return;
    sync();
    if (!personalizing() || !hitsWallet(e)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (e.type === "click") {
      showError("Personalized bags check out with Add to cart, so your design and the embroidery go through together. Tap Add to cart, then pay with Apple Pay or Google Pay at checkout.");
      if (window.console && console.warn) console.warn("[fpm-guard] blocked express checkout while personalizing");
    }
  }

  ["pointerdown", "mousedown", "touchstart", "click", "keydown"].forEach(function (type) {
    window.addEventListener(type, blockWallet, { capture: true, passive: false });
  });

  // ---- 2. Unfinished personalization: block the add ------------------------

  // Capture on window runs ahead of fpm.js's own capture listener on document.
  window.addEventListener("submit", function (e) {
    var form = e.target;
    if (!isCartAdd(form)) return;
    if (!isOn() || isReady()) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    showError("Add the text you want stitched or upload your logo before adding to cart — or switch personalization off to order a plain bag.");
    focusPanel();
  }, true);

  // ---- 3. fpm.js declined a personalized add: refuse it --------------------

  // Bubble on window runs LAST. fpm.js stops propagation in its capture-phase
  // handler whenever it takes the submit, so reaching here means it declined.
  window.addEventListener("submit", function (e) {
    var form = e.target;
    if (!isCartAdd(form)) return;
    if (e.defaultPrevented) return;
    if (!isOn() && !hasStagedPersonalization(form)) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    showError("We could not attach your personalization to this order, so we have stopped it rather than make your bag without it. Please reload the page and try again — nothing has been charged.");
    focusPanel();

    if (window.console && console.warn) {
      console.warn("[fpm-guard] blocked a personalized add-to-cart that bypassed fpm.js — artwork would not have been attached");
    }
  }, false);

  // ---- Keep the gate current, for the whole visit -------------------------

  ["change", "input", "click"].forEach(function (type) {
    document.addEventListener(type, function () { sync(); }, true);
  });
  document.addEventListener("shopify:section:load", sync);
  window.addEventListener("pageshow", sync);

  injectCss();
  sync();
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { injectCss(); sync(); });
  }

  // No cap: a checkbox flipped in code fires no event, and a customer can take
  // as long as they like to type a name. Two cheap DOM lookups every 250 ms.
  setInterval(sync, 250);
})();
